import {
  activeTaskFilterCount,
  NO_TASK_FILTERS,
  type FilterOption,
  type TaskFilterState,
} from '../../../lib/task-filters';
import { useTranslation } from '../../../lib/i18n';
import { ClearFiltersButton } from '../category-view/CategoryView';
import { agentMark, CategoryFilterMenu, useSpaceMark } from '../category-view/CategoryFilterMenu';
import styles from './TasksFilterBar.module.css';

// The Agent Tasks list's filter row: **Space, then Agent** — each a labelled DS picker whose label states
// the current value, plus a Clear that appears once something is narrowed. Same shape as `FilesFilterBar`,
// which is the shipped precedent for a filter row in this product; there is no DS Select, and the
// OverflowMenu-as-picker is the house answer (a hand-rolled picker control is forbidden outright).
//
// Space leads because it is the question this list is most often narrowed by — and because it is the axis
// the URL carries (`/tasks?workspace=<slug>`), so it is the one a user arrives already narrowed on.
//
// The options are **passed in, not derived here**: the host clamps its state against the same two lists
// (`clampTaskFilters`), and a list derived twice is a list that can disagree with itself. A menu is drawn as
// soon as it has one option, so every category's row reads "All spaces ⌄ · All agents ⌄" (Figma 21055:246799).
export function TasksFilterBar({
  spaces,
  agents,
  filters,
  onChange,
  onBi,
  className,
}: {
  /** The Spaces worth offering — `spacesOf(tasks, workspaces)`. Empty when the host fixes this axis. */
  spaces: FilterOption[];
  /** The agents worth offering — `agentsOf(tasks, team)`. Empty when the host fixes this axis. */
  agents: FilterOption[];
  filters: TaskFilterState;
  onChange: (next: TaskFilterState) => void;
  /** Reports which filter was used, for BI. */
  onBi?: (name: string) => void;
  /** Layout from the enclosing row — the bar owns its chips, not its place on the page. */
  className?: string;
}) {
  const { t } = useTranslation();
  const spaceMark = useSpaceMark();

  const pick = (name: string, next: TaskFilterState) => {
    onBi?.(name);
    onChange(next);
  };

  return (
    <div className={className ? `${styles.bar} ${className}` : styles.bar}>
      {spaces.length > 0 && (
        <CategoryFilterMenu
          options={spaces.map((space) => ({ id: space.id, name: space.name, icon: spaceMark(space.id, space.name) }))}
          selectedId={filters.workspaceId}
          allLabel={t('tasks.filter.allSpaces')}
          ariaLabel={t('packs.section.workspace')}
          onPick={(id) => pick(id ? 'filter_space' : 'filter_space_all', { ...filters, workspaceId: id })}
          onOpen={() => onBi?.('filter_space_open')}
        />
      )}
      {agents.length > 0 && (
        <CategoryFilterMenu
          options={agents.map((agent) => ({ id: agent.id, name: agent.name, icon: agentMark(agent.id) }))}
          selectedId={filters.agentId}
          allLabel={t('tasks.filter.allAgents')}
          ariaLabel={t('island.agent')}
          onPick={(id) => pick(id ? 'filter_agent' : 'filter_agent_all', { ...filters, agentId: id })}
          onOpen={() => onBi?.('filter_agent_open')}
        />
      )}
      <ClearFiltersButton count={activeTaskFilterCount(filters)} onClear={() => pick('filter_clear', NO_TASK_FILTERS)} />
    </div>
  );
}
